import { useReport } from '../reporting/ReportContext';
import { FilterBar } from '../reporting/FilterBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { downloadCsv, niceDate, num, periodDelta } from '../lib/reporting';

export function FailedDeliveries() {
  const { summary, prevSummary, error, filters } = useReport();

  const exportCsv = () => {
    if (!summary) return;
    downloadCsv(
      `nvs-failed-deliveries-${filters.from}_${filters.to}.csv`,
      summary.failedList.map((r) => ({
        date: r.date ?? '',
        stop_ref: r.stopRef ?? '',
        region: r.region ?? '',
        driver: r.driver ?? '',
        status: r.status,
        reason: r.reason ?? 'Not recorded',
      })),
    );
  };

  const exceptions = summary ? summary.totals.failed + summary.totals.cancelled : 0;
  const prevExceptions = prevSummary ? prevSummary.totals.failed + prevSummary.totals.cancelled : null;
  // failed_reason isn't populated by the Locate2u sync for this team, so every
  // row's reason is blank. Detect that so we can say so once instead of
  // repeating "Not recorded" down a whole column.
  const hasReasons = !!summary?.failedList.some((r) => r.reason && r.reason.trim() !== '');
  const listCapped = summary?.failedList.length === 500;

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / Failed deliveries"
        title="Failed deliveries"
        subtitle={summary ? `NVS in-house · ${summary.from} → ${summary.to} · failed & cancelled stops` : 'NVS in-house · failed & cancelled stops'}
      />
      <FilterBar onExport={exportCsv} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!summary && !error && <p className="muted">Loading…</p>}

      {summary && (
        <>
          <div className="kpi-grid">
            <KpiCard label="Exceptions" value={num(exceptions)} sub="Failed + cancelled" tone={exceptions === 0 ? 'good' : 'bad'} accent="var(--danger)" delta={periodDelta(exceptions, prevExceptions, { kind: 'count', higherIsBetter: false })} />
            <KpiCard label="Failed" value={num(summary.totals.failed)} sub="First-time delivery failures" accent="var(--danger)" />
            <KpiCard label="Cancelled" value={num(summary.totals.cancelled)} sub="Cancelled before delivery" accent="var(--danger)" />
            <KpiCard label="In progress" value={num(summary.totals.inProgress)} sub="Pending / enroute / arrived" accent="var(--border)" />
          </div>

          {summary.failedByRegion.length > 0 && (
            <Panel title="Exceptions by region" subtitle="Failed & cancelled stops by team region">
              <div className="status-list">
                {summary.failedByRegion.map((r) => {
                  const max = Math.max(...summary.failedByRegion.map((x) => x.count), 1);
                  return (
                    <div key={r.region} className="status-row">
                      <span className="status-name">{r.region}</span>
                      <span className="status-bar" aria-hidden="true">
                        <span className="status-fill" style={{ width: `${(r.count / max) * 100}%`, background: 'var(--danger)' }} />
                      </span>
                      <span className="status-count">{num(r.count)}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          <Panel
            title="Failed & cancelled stops"
            subtitle={
              listCapped
                ? 'Showing the 500 most recent stops in range — narrow the date range to see the rest'
                : `${num(summary.failedList.length)} stop(s) in range`
            }
          >
            {summary.failedList.length === 0 ? (
              <div className="empty-note">
                <strong>No failed or cancelled deliveries in this range.</strong>
                <p className="muted">Every stop in the selected window was delivered or is still in progress.</p>
              </div>
            ) : (
              <>
                {!hasReasons && (
                  <p className="muted table-note">
                    Failure reasons aren&apos;t captured in the source sync for this team yet, so the
                    reason column is omitted. Once <code>failed_reason</code> flows from Locate2u it will
                    appear here automatically.
                  </p>
                )}
                <div className="data-table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Stop ref</th>
                        <th>Region</th>
                        <th>Driver</th>
                        <th>Status</th>
                        {hasReasons && <th>Reason</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {summary.failedList.map((r, i) => (
                        <tr key={`${r.stopRef}-${i}`}>
                          <td>{r.date ? niceDate(r.date) : '—'}</td>
                          <td>{r.stopRef ?? '—'}</td>
                          <td>{r.region ?? 'Unassigned'}</td>
                          <td>{r.driver ?? '—'}</td>
                          <td>{r.status}</td>
                          {hasReasons && <td className="muted">{r.reason ?? 'Not recorded'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Panel>
        </>
      )}
    </section>
  );
}
